// FormsPacketTab.tsx — the Forms & packet tab (wireframe S6, Insurance
// Claims Phase 5.2/5.3/5.5; redesigned 2026-10-15 to the wireframe's own
// layout): a "Generate forms" card (each form's persisted last generation,
// its LIVE missing-field count with where to fix each field, and the
// seal/signatory note), a "Build packet" card (profile, format, checklist
// summary, data-sharing consent — a warning, never a block — and the latest
// packet), the profile's checklist in two columns, and the Record
// submission dialog. Generation status comes from GET /claims/:id/forms, so
// a reload no longer forgets that a form was generated.
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { FormField } from '@/components/ui/FormField';
import { NativeSelect } from '@/components/ui/Input';
import { Section } from '@/components/ui/Section';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { openPatientDocument } from '@/api';
import { useGenerateClaimForm, useBuildClaimPacket, useClaimFormsStatus } from './hooks';
import { RecordSubmissionDialog } from './RecordSubmissionDialog';
import type {
  ClaimWorkspace as ClaimWorkspaceType, ClaimFormsStatus, DocumentChecklistItem, FormGenerationStatus, PacketResult,
} from './types';

const FORM_LABEL: Record<'preauth' | 'part_b', string> = {
  preauth: 'Pre-auth request (IRDAI layout)',
  part_b: 'Claim Form Part B',
};

const PROFILE_LABEL: Record<string, string> = {
  irdai_cashless_claim: 'IRDAI cashless: claim submission',
  irdai_cashless_final: 'IRDAI cashless: final authorisation',
  irdai_reimbursement: 'IRDAI reimbursement',
  pmjay_generic: 'PM-JAY: claim submission',
  cmchis_generic: 'CMCHIS: claim submission',
};

function fmtWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function FormRow({ claim, form, status }: { claim: ClaimWorkspaceType; form: 'preauth' | 'part_b'; status: FormGenerationStatus }) {
  const mutation = useGenerateClaimForm(claim.id);
  const [error, setError] = useState('');
  const [showMissing, setShowMissing] = useState(false);
  const last = status.last_generated;
  const missing = status.missing;

  const generate = async () => {
    setError('');
    try { await mutation.mutateAsync(form); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <>
      <TableRow>
        <TableCell className="font-medium">{FORM_LABEL[form]}</TableCell>
        <TableCell className="text-[12px]">
          {last ? (
            <>
              {fmtWhen(last.at)}{last.by ? ` · ${last.by}` : ''}
              {last.draft && <Badge variant="warn" className="ml-1.5">DRAFT</Badge>}
            </>
          ) : <span className="text-ink-muted">Not generated yet</span>}
        </TableCell>
        <TableCell>
          {missing.length === 0
            ? <Badge variant="success">0</Badge>
            : (
              <button type="button" className="cursor-pointer" onClick={() => setShowMissing((v) => !v)} aria-expanded={showMissing}>
                <Badge variant="warn">{missing.length} {showMissing ? '▴' : '▾'}</Badge>
              </button>
            )}
        </TableCell>
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-2">
            {last?.document_id && claim.uhid && (
              <button type="button" className="text-[12px] text-link underline" onClick={() => openPatientDocument(claim.uhid as string, last.document_id as number)}>
                View
              </button>
            )}
            <Button size="sm" variant="ghost" loading={mutation.isPending} onClick={generate}>
              {last ? 'Regenerate' : 'Generate'}
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {(showMissing && missing.length > 0) && (
        <TableRow>
          <TableCell colSpan={4} className="bg-warn-soft/40 text-[12px]">
            <div className="mb-1 text-warn-ink">Prints as a DRAFT until these are recorded (it can't reach the checklist or a packet):</div>
            <ul className="list-inside list-disc">
              {missing.map((m) => <li key={m.field}>{m.reason}{m.fix_at ? <span className="text-ink-muted"> → {m.fix_at}</span> : null}</li>)}
            </ul>
          </TableCell>
        </TableRow>
      )}
      {error && (
        <TableRow><TableCell colSpan={4} className="text-[12px] text-danger-ink">{error}</TableCell></TableRow>
      )}
    </>
  );
}

function GenerateFormsCard({ claim, status }: { claim: ClaimWorkspaceType; status: ClaimFormsStatus }) {
  return (
    <Section title="Generate forms" subtitle="Statutory forms, built from the claim's own data. A form with gaps prints as a DRAFT only.">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Form</TableHead>
            <TableHead>Last generated</TableHead>
            <TableHead>Missing fields</TableHead>
            <TableHead className="text-right" />
          </TableRow>
        </TableHeader>
        <TableBody>
          <FormRow claim={claim} form="preauth" status={status.preauth} />
          <FormRow claim={claim} form="part_b" status={status.part_b} />
          <TableRow>
            <TableCell className="text-ink-muted">Part A assist (patient section prefill)</TableCell>
            <TableCell className="text-[12px] text-ink-muted">—</TableCell>
            <TableCell><Badge variant="neutral">optional</Badge></TableCell>
            <TableCell className="text-right text-[11px] text-ink-muted">Not available yet</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <div className="mt-2 text-[11.5px]">
        {status.can_sign
          ? <span>☑ The hospital seal &amp; signatory print on generated forms (you have <code>claims:sign-forms</code>).</span>
          : <span className="text-ink-muted">☐ Forms print an empty seal circle for a wet seal — printing the seal needs <code>claims:sign-forms</code>.</span>}
      </div>
    </Section>
  );
}

function consentLine(c: ClaimFormsStatus['data_sharing_consent']) {
  // utils/patientConsent.js#consentStatus: granted | declined | expired | missing.
  if (c.status === 'granted') {
    return <span className="text-success-ink">✔ recorded{c.recorded_at ? ` ${new Date(c.recorded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}` : ''}</span>;
  }
  if (c.status === 'not_recorded' || c.status === 'missing') return <span className="text-warn-ink">⚠ not recorded (you can still build the packet)</span>;
  return <span className="text-warn-ink">⚠ {c.status.replace(/_/g, ' ')} (you can still build the packet)</span>;
}

function BuildPacketCard({ claim, status }: { claim: ClaimWorkspaceType; status: ClaimFormsStatus }) {
  const [stage, setStage] = useState<'claim' | 'final'>(claim.status === 'final_auth_sent' || claim.status === 'final_auth_query' ? 'final' : 'claim');
  const [format, setFormat] = useState<'pdf' | 'zip'>('pdf');
  const [result, setResult] = useState<PacketResult | null>(null);
  const [error, setError] = useState('');
  const mutation = useBuildClaimPacket(claim.id);

  const checklist = claim.document_checklist;
  const applicable = checklist.filter((d) => d.effective_status !== 'not_applicable');
  const requiredApplicable = applicable.filter((d) => d.required);
  const requiredReady = requiredApplicable.filter((d) => d.effective_status !== 'missing').length;
  const notApplicable = checklist.length - applicable.length;
  const claimProfile = status.profile ?? 'irdai_cashless_claim';
  const profileKey = stage === 'final' ? 'irdai_cashless_final' : claimProfile;

  const build = async () => {
    setError(''); setResult(null);
    try { setResult(await mutation.mutateAsync({ stage, format })); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const last = status.last_packet;
  return (
    <Section title="Build packet">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField label="Profile" className="sm:col-span-2">
          <NativeSelect value={stage} onChange={(e) => setStage(e.target.value as 'claim' | 'final')}>
            <option value="claim">{PROFILE_LABEL[claimProfile] ?? claimProfile}</option>
            {claim.mode === 'cashless' && <option value="final">{PROFILE_LABEL.irdai_cashless_final}</option>}
          </NativeSelect>
        </FormField>
        <FormField label="Format">
          <SegmentedControl
            size="sm"
            options={[{ value: 'pdf', label: 'Merged PDF' }, { value: 'zip', label: 'Zip of PDFs' }]}
            value={format}
            onChange={(v) => setFormat(v as 'pdf' | 'zip')}
          />
        </FormField>
        <FormField label="Split if larger than">
          <div className="pt-1 text-[12px]">
            {status.max_upload_mb != null
              ? <>{status.max_upload_mb} MB <span className="text-ink-muted">(payer setting)</span></>
              : <span className="text-ink-muted">No payer upload limit set — one file (Admin › Insurance Claims › Payers)</span>}
          </div>
        </FormField>
      </div>
      <div className="mt-3 text-[12.5px]">
        Checklist: <b>{requiredReady}/{requiredApplicable.length} required</b>{requiredReady === requiredApplicable.length ? ' ✔' : ''}
        {' · '}{notApplicable} n/a · consent to share: {consentLine(status.data_sharing_consent)}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" loading={mutation.isPending} onClick={build}>Build packet</Button>
        {last?.document_id && claim.uhid && (last.document_ids.length > 1
          ? last.document_ids.map((id, i) => (
            <Button key={id} size="sm" variant="ghost" onClick={() => openPatientDocument(claim.uhid as string, id)}>
              Last packet part {i + 1} of {last.document_ids.length}
            </Button>
          ))
          : (
            <Button size="sm" variant="ghost" onClick={() => openPatientDocument(claim.uhid as string, last.document_id as number)}>
              Open last packet{last.page_count ? ` (${last.page_count} pages)` : ''}
            </Button>
          ))}
      </div>
      <div className="mt-1 text-[11px] text-ink-muted">
        {PROFILE_LABEL[profileKey] ?? profileKey} · the index page is the merged PDF's first page.
        {last ? ` Last built ${fmtWhen(last.at)}${last.by ? ` by ${last.by}` : ''}.` : ''}
      </div>
      {error && <div className="mt-2 text-[12px] text-danger-ink">{error}</div>}
      {result && (
        <div className="mt-3 rounded-[var(--radius)] bg-muted px-3 py-2 text-[12.5px]">
          Built: {result.items.length} document(s){result.page_count ? `, ${result.page_count} pages` : ''}.{' '}
          {claim.uhid && result.parts.length <= 1 && (
            <button type="button" className="text-link underline" onClick={() => openPatientDocument(claim.uhid as string, result.document_id)}>
              Download
            </button>
          )}
          {claim.uhid && result.parts.length > 1 && (
            <div className="mt-1 flex flex-col gap-0.5">
              <span className="text-ink-muted">Split into {result.parts.length} files to stay under the payer's {result.max_upload_mb} MB limit:</span>
              {result.parts.map((p) => (
                <button key={p.document_id} type="button" className="self-start text-link underline" onClick={() => openPatientDocument(claim.uhid as string, p.document_id)}>
                  Part {p.part} of {p.of}{p.page_from ? ` — pages ${p.page_from}–${p.page_to}` : ''} · {(p.size_bytes / 1e6).toFixed(1)} MB
                </button>
              ))}
            </div>
          )}
          {result.oversize.length > 0 && (
            <div className="mt-1 text-[12px] text-warn-ink">
              ⚠ Still over the limit (a single page or image can't be split): {result.oversize.map((o) => o.label ?? `part ${o.part}`).join(', ')} — compress or re-scan before uploading.
            </div>
          )}
          {result.omitted.length > 0 && (
            <div className="mt-1 text-[12px] text-ink-muted">Not included: {result.omitted.map((o) => `${o.label} (${o.status.replace('_', ' ')})`).join(', ')}</div>
          )}
        </div>
      )}
    </Section>
  );
}

const SOURCE_PILL: Record<DocumentChecklistItem['effective_status'], { variant: 'success' | 'neutral' | 'warn'; label: string }> = {
  generated: { variant: 'success', label: 'generated' },
  attached: { variant: 'neutral', label: 'uploaded' },
  waived: { variant: 'warn', label: 'waived' },
  not_applicable: { variant: 'neutral', label: 'n/a' },
  missing: { variant: 'warn', label: 'missing' },
};

function ChecklistCard({ claim, profile, onRecordSubmission }: { claim: ClaimWorkspaceType; profile: string | null; onRecordSubmission: () => void }) {
  const items = claim.document_checklist;
  const half = Math.ceil(items.length / 2);
  const columns = [items.slice(0, half), items.slice(half)];
  return (
    <Section
      title={`Checklist: profile ${profile ?? '—'}`}
      subtitle="Attach, waive or un-waive items on the Documents tab."
      action={claim.status === 'discharged' && <Button size="sm" onClick={onRecordSubmission}>Record submission…</Button>}
    >
      <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
        {columns.map((col, i) => (
          <div key={i}>
            {col.map((d) => {
              const pill = SOURCE_PILL[d.effective_status];
              const done = d.effective_status !== 'missing';
              return (
                <div key={d.id ?? d.doc_key} className="grid grid-cols-[18px_1fr_auto] items-center gap-1.5 border-b border-solid border-x-0 border-t-0 border-border py-1 text-[12.5px]">
                  <span className={done ? 'text-success-ink' : 'text-ink-muted'}>
                    {d.effective_status === 'not_applicable' ? '–' : done ? '✔' : '○'}
                  </span>
                  <span>{d.label}{d.required && !done ? <span className="text-danger-ink"> *</span> : null}</span>
                  <Badge variant={pill.variant}>{pill.label}</Badge>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </Section>
  );
}

export function FormsPacketTab({ claim }: { claim: ClaimWorkspaceType }) {
  const statusQuery = useClaimFormsStatus(claim.id);
  const [submitting, setSubmitting] = useState(false);

  if (statusQuery.isLoading) return <Section title="Forms & packet"><LoadingState label="Loading forms…" /></Section>;
  if (statusQuery.isError || !statusQuery.data) {
    return (
      <Section title="Forms & packet">
        <ErrorState message={statusQuery.error instanceof ApiError ? statusQuery.error.message : 'Could not load form status'} onRetry={() => statusQuery.refetch()} />
      </Section>
    );
  }
  const status = statusQuery.data;
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <GenerateFormsCard claim={claim} status={status} />
        <BuildPacketCard claim={claim} status={status} />
      </div>
      <ChecklistCard claim={claim} profile={status.profile} onRecordSubmission={() => setSubmitting(true)} />
      {submitting && <RecordSubmissionDialog claim={claim} open onClose={() => setSubmitting(false)} />}
    </div>
  );
}
